import { applyCastAliasCleanupPass } from "@/lib/castAliasCleanupPass";
import { normalizeNarrator } from "@/lib/castAnalysisNormalize";
import type { BookCastJson, CastCharacter, CastCharacterCategory, CastImportanceLevel } from "@/lib/castAnalysisTypes";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

const CATEGORIES: CastCharacterCategory[] = [
  "narrator",
  "main_character",
  "supporting_character",
  "minor_character",
  "creature_or_entity",
  "metadata_exclude",
];

const IMPORTANCE: CastImportanceLevel[] = ["main", "supporting", "minor", "entity"];

function str(v: unknown): string {
  return typeof v === "string" ? v.trim() : "";
}

function parseCategory(v: unknown): CastCharacterCategory | undefined {
  const s = str(v);
  return CATEGORIES.includes(s as CastCharacterCategory) ? (s as CastCharacterCategory) : undefined;
}

function parseImportance(v: unknown): CastImportanceLevel | undefined {
  const s = str(v);
  return IMPORTANCE.includes(s as CastImportanceLevel) ? (s as CastImportanceLevel) : undefined;
}

function normalizeMergedCharacter(raw: unknown, index: number): CastCharacter | null {
  if (!isRecord(raw)) return null;
  const name = str(raw.name);
  if (!name) return null;
  let relationships: string[] = [];
  if (Array.isArray(raw.relationships)) {
    relationships = raw.relationships.map((x) => str(x)).filter((s) => s.length > 0);
  }
  let aliases: string[] = [];
  if (Array.isArray(raw.aliases)) {
    aliases = raw.aliases.map((x) => str(x)).filter((s) => s.length > 0 && s.toLowerCase() !== name.toLowerCase());
  }
  let source_chunks: string[] | undefined;
  if (Array.isArray(raw.source_chunks)) {
    const u = raw.source_chunks.map((x) => str(x)).filter((s) => s.length > 0);
    source_chunks = u.length > 0 ? [...new Set(u)].slice(0, 48) : undefined;
  }
  let confidence: number | undefined;
  if (typeof raw.confidence === "number" && Number.isFinite(raw.confidence)) {
    confidence = Math.min(1, Math.max(0, raw.confidence));
  }
  let chunk_mentions: number | undefined;
  if (typeof raw.chunk_mentions === "number" && Number.isFinite(raw.chunk_mentions)) {
    chunk_mentions = Math.max(0, Math.floor(raw.chunk_mentions));
  }
  let importance_rank: number | undefined;
  if (typeof raw.importance_rank === "number" && Number.isFinite(raw.importance_rank)) {
    importance_rank = Math.max(1, Math.floor(raw.importance_rank));
  }
  const cast_category = parseCategory(raw.cast_category);
  const importance = parseImportance(raw.importance);
  let needs_voice: boolean | undefined;
  if (typeof raw.needs_voice === "boolean") needs_voice = raw.needs_voice;

  return {
    name,
    role: str(raw.role) || (index === 0 ? "character" : ""),
    description: str(raw.description),
    backstory: str(raw.backstory),
    relationships: relationships.slice(0, 28),
    personality: str(raw.personality),
    age_or_vocal_quality: str(raw.age_or_vocal_quality),
    speaking_style: str(raw.speaking_style),
    voice_direction: str(raw.voice_direction),
    confidence,
    voice: null,
    source_chunks,
    chunk_mentions,
    importance_rank,
    aliases: aliases.length > 0 ? [...new Set(aliases)].slice(0, 24) : undefined,
    cast_category,
    importance,
    needs_voice,
  };
}

/** Slim preliminary cast for merge webhook (size limits). */
export function preliminaryCastForMergeApi(preliminary: BookCastJson, maxCharacters = 96): Record<string, unknown> {
  const SL = 1400;
  const clip = (s: string, m: number) => {
    const t = s.trim();
    return t.length <= m ? t : `${t.slice(0, m)}…`;
  };
  const narr = preliminary.narrator ?? { name: "Narrator", description: "", tone: "", voice_direction: "" };
  const slimNarr = {
    name: clip(String(narr.name || "Narrator"), 120),
    description: clip(String(narr.description ?? ""), 2400),
    tone: clip(String(narr.tone ?? ""), 1200),
    voice_direction: clip(String(narr.voice_direction ?? ""), 1200),
  };
  const chars = (preliminary.characters ?? []).slice(0, maxCharacters).map((c) => ({
    name: clip(String(c.name ?? ""), 200),
    role: clip(String(c.role ?? ""), 240),
    description: clip(String(c.description ?? ""), SL),
    backstory: clip(String(c.backstory ?? ""), SL),
    personality: clip(String(c.personality ?? ""), SL),
    relationships: (Array.isArray(c.relationships) ? c.relationships : []).slice(0, 16).map((x) => clip(String(x), 120)),
    speaking_style: clip(String(c.speaking_style ?? ""), SL),
    voice_direction: clip(String(c.voice_direction ?? ""), SL),
    age_or_vocal_quality: clip(String(c.age_or_vocal_quality ?? ""), 400),
    confidence: typeof c.confidence === "number" && Number.isFinite(c.confidence) ? c.confidence : undefined,
    source_chunks: (Array.isArray(c.source_chunks) ? c.source_chunks : []).slice(0, 40).map((x) => String(x)),
    chunk_mentions: c.chunk_mentions,
    importance_rank: c.importance_rank,
  }));
  return { narrator: slimNarr, characters: chars };
}

export type CastMergeWebhookOk = {
  success: true;
  book_id: string;
  title: string;
  author: string;
  narrator: unknown;
  characters: unknown[];
  chunks_analyzed?: number;
  merge_pass_applied: true;
};

export type CastMergeWebhookErr = { success: false; error: string; stage?: string };

export function parseCastMergeWebhookPayload(payload: unknown): CastMergeWebhookOk | CastMergeWebhookErr {
  if (!isRecord(payload)) return { success: false, error: "Invalid response", stage: "parse" };
  if (payload.success === false) {
    const err =
      typeof payload.error === "string" && payload.error.trim()
        ? payload.error.trim()
        : "Cast merge failed";
    const stage = typeof payload.stage === "string" && payload.stage.trim() ? payload.stage.trim() : undefined;
    return { success: false, error: err, stage };
  }
  if (payload.success !== true) {
    return { success: false, error: "Merge response missing success:true", stage: "parse" };
  }
  const book_id = str(payload.book_id);
  if (!book_id) return { success: false, error: "Missing book_id", stage: "parse" };
  const title = str(payload.title);
  const author = str(payload.author);
  const narrator = payload.narrator;
  const rawChars = Array.isArray(payload.characters) ? payload.characters : [];
  return {
    success: true,
    book_id,
    title: title || book_id,
    author,
    narrator,
    characters: rawChars,
    chunks_analyzed:
      typeof payload.chunks_analyzed === "number" && Number.isFinite(payload.chunks_analyzed)
        ? Math.max(1, Math.floor(payload.chunks_analyzed))
        : undefined,
    merge_pass_applied: true,
  };
}

/**
 * Turn merge webhook JSON into persisted `BookCastJson`, excluding `metadata_exclude`.
 * On parse failure returns `preliminary` unchanged (caller may set `merge_pass_applied` false).
 */
export function bookCastFromMergePayload(
  preliminary: BookCastJson,
  payload: unknown,
  meta: { source?: string },
): BookCastJson {
  const parsed = parseCastMergeWebhookPayload(payload);
  if (!parsed.success) {
    return applyCastAliasCleanupPass({
      ...preliminary,
      merge_pass_applied: false,
      cast_merge_debug: {
        preliminary_count: preliminary.characters?.length ?? 0,
        preliminary_names: (preliminary.characters ?? []).map((c) => c.name).slice(0, 200),
        merge_error: parsed.error,
      },
    });
  }

  const narrator = normalizeNarrator(parsed.narrator);
  const mergedChars: CastCharacter[] = [];
  parsed.characters.forEach((c, i) => {
    const row = normalizeMergedCharacter(c, i);
    if (row && row.cast_category !== "metadata_exclude") mergedChars.push(row);
  });

  const excluded = (() => {
    const out: string[] = [];
    parsed.characters.forEach((c) => {
      if (!isRecord(c)) return;
      if (parseCategory(c.cast_category) === "metadata_exclude") {
        const n = str(c.name);
        if (n) out.push(n);
      }
    });
    return [...new Set(out)].slice(0, 40);
  })();

  return applyCastAliasCleanupPass({
    success: true,
    book_id: parsed.book_id,
    title: parsed.title || preliminary.title,
    author: parsed.author || preliminary.author,
    source: meta.source ?? preliminary.source,
    status: "ready",
    narrator,
    characters: mergedChars,
    analysis_mode: "full_book",
    chunks_analyzed: parsed.chunks_analyzed ?? preliminary.chunks_analyzed,
    merge_pass_applied: true,
    cast_merge_debug: {
      preliminary_count: preliminary.characters?.length ?? 0,
      preliminary_names: (preliminary.characters ?? []).map((c) => c.name).slice(0, 200),
      excluded_metadata_names: excluded.length > 0 ? excluded : undefined,
    },
  });
}
